'use client'

import { useEffect, useRef, useState } from 'react'

import { CLUTCH_REVIEW_DATA } from 'Constants/ExternalLinks'

import { Container } from './styles'

export default function ReviewWidget() {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)
  const containerRef = useRef<HTMLAnchorElement>(null)

  // The badge lives in the footer of every page, but its script was requested
  // on mount: 7 KB of JS, an 88 KB Roboto TTF and a third-party cookie
  // (`__cf_bm`) on the critical path of every route — the cookie alone is what
  // held Best Practices at 74. Load it when the badge is about to scroll into
  // view instead; visitors who reach the footer still get the real widget.
  useEffect(() => {
    const container = containerRef.current

    if (!container) return undefined

    let script: HTMLScriptElement | null = null

    const load = () => {
      if (script) return

      script = document.createElement('script')
      script.src = CLUTCH_REVIEW_DATA.scriptSrc
      script.async = true
      script.onload = () => {
        setIsScriptLoaded(true)
      }
      document.body.appendChild(script)
    }

    if (typeof IntersectionObserver === 'undefined') {
      load()

      return () => {
        if (script) document.body.removeChild(script)
      }
    }

    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return

        observer.disconnect()
        load()
      },
      { rootMargin: '300px' },
    )

    observer.observe(container)

    return () => {
      observer.disconnect()
      if (script) document.body.removeChild(script)
    }
  }, [])

  useEffect(() => {
    if (isScriptLoaded) {
      if ((window as any).CLUTCHCO) {
        ;(window as any).CLUTCHCO.Init()
      }
    }
  }, [isScriptLoaded])

  return (
    <Container
      // The anchor's only content is the third-party widget, which is empty
      // until Clutch's script fills it — so the link has no name of its own.
      aria-label="StartupCraft reviews on Clutch"
      href={CLUTCH_REVIEW_DATA.link}
      ref={containerRef}
      rel="noopener noreferrer"
      target="_blank"
    >
      <div
        className="clutch-widget"
        data-clutchcompany-id={CLUTCH_REVIEW_DATA.companyId}
        data-expandifr="true"
        data-height="48"
        data-nofollow="true"
        data-scale="70"
        data-url={CLUTCH_REVIEW_DATA.dataUrl}
        data-widget-type="2"
      />
    </Container>
  )
}
